// Belmont University: public college page (lane 2, #2745), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - Belmont's Common Data Set 2025-2026 (Institutional Research): C1
//    applicants, admits and enrollees for Fall 2025, C21/C22 plans and the
//    G1 2026-2027 costs. Quirks: the PDF's text layer garbles some fonts.
//    C1 gives no total line, so the totals are the sum of its men, women
//    and another-gender rows (2,881 + 6,257 + 2 applied; 2,644 + 5,915 + 2
//    admitted; 606 + 3 + 1,174 + 4 enrolled). The C1 residency table is
//    blank, and the C8 check boxes do not survive in the text layer, so the
//    test policy is read from Belmont's admissions page.
//  - Belmont Admissions, How to Apply as a First-Year Student: the
//    Admissions Timeline for Fall 2027, Early Action and Regular Decision
//    dates, the test policy and the programs with extra steps.
//  - Belmont Student Financial Services, Cost of Attendance: the 2026-2027
//    first-year direct cost ($61,330: tuition $21,485 and fees $1,115 per
//    semester, housing and meals $16,130), the same items as the CDS G1.
//  - College Scorecard API, school id 219709 (net price by family income).
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'Belmont Common Data Set 2025-2026',
        url: 'https://www.belmont.edu/irsa/_files/data-sets/cds-2025-2026.pdf',
        read: '2026-10-01',
    },
    firstYear: {
        label: 'Belmont Admissions, How to Apply as a First-Year Student',
        url: 'https://www.belmont.edu/admissions/first-year/apply.html',
        read: '2026-10-01',
    },
    costs: {
        label: 'Belmont Student Financial Services, Cost of Attendance',
        url: 'https://www.belmont.edu/sfs/cost/',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?219709-Belmont-University',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'belmont-university',
    kbId: 'belmont_university',
    name: 'Belmont University',
    shortName: 'Belmont',
    location: 'Nashville, TN',
    control: 'Private',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (Belmont brand guide); decoration only.
    branding: {
        primary: '#001D54',
        secondary: '#B21029',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/belmont_university_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 9140,
        admitted: 8561,
        enrolled: 1787,
        source: 'cds',
        where: 'section C1 (sum of its rows; it gives no total line)',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'Belmont\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by college or major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early action',
        note: 'Belmont has two non-binding Early Action rounds and no early decision plan. The Common Data Set gives no early action counts, so there is no early round acceptance rate.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'You apply to Belmont, and most majors need nothing more. Some ask for more:',
        rows: [
            { school: 'School of Music, Department of Theatre and Dance, select Watkins College of Art programs, the Songwriting major, and Exercise Science (3+3 DPT or OTD)', requirement: 'Additional application steps, including auditions and portfolio submissions, with their own deadlines.' },
        ],
        source: 'firstYear',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision', value: 'March 1' },
        rows: [
            { plan: 'Early Action I', deadline: 'November 1', decision: 'November 20', binding: false },
            { plan: 'Early Action II', deadline: 'December 1', decision: 'December 21', binding: false },
            { plan: 'Regular Decision', deadline: 'March 1', decision: 'Rolling, from January', binding: false },
        ],
        note: 'November 1 is also the top scholarship deadline, and December 1 the departmental scholarship deadline and Belmont\'s FAFSA priority date. March 1 is also the priority enrollment deposit deadline for admitted students; the national reply date is May 1. Classes begin August 25, 2027.',
        source: 'firstYear',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'Belmont is test-optional: you decide in your application whether your SAT or ACT scores are considered. It also accepts the CLT. If you send scores, it superscores across test dates for admission and scholarships, and it no longer counts the ACT Science section in the composite.',
        source: 'firstYear',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'firstYear',
    },

    cost: {
        year: '2026-2027',
        rows: [
            { item: 'Tuition', amount: 42970 },
            { item: 'Required fees', amount: 2230 },
            { item: 'Food and housing (on campus)', amount: 16130 },
        ],
        total: 61330,
        note: 'The direct cost for a first-year student living on campus. Belmont\'s total does not include course fees or books, which it estimates at up to $1,500 a year depending on your major and minor. Housing and meal plan rates vary by hall and plan; returning students\' housing and meals are listed at $23,170.',
        source: 'costs',
        where: 'first-year direct cost; the same items as the Common Data Set, section G1',
        netPrice: {
            average: 32648,
            byIncome: [
                { income: '$0 to $30,000', amount: 28236 },
                { income: '$30,001 to $48,000', amount: 26421 },
                { income: '$48,001 to $75,000', amount: 27963 },
                { income: '$75,001 to $110,000', amount: 28843 },
                { income: 'Over $110,000', amount: 43872 },
            ],
            note: 'What first-year, full-time students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from Belmont\'s aid offer.',
            tileDetail: 'Per year, after grants, federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
